// CommandsListPage – view commands for a single request, with selection and bulk actions.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CommandUpdateMessage } from '../../../types/WebsocketSchema';
import { useAuth } from '../../context/AuthContext';
import {
    CommandRow,
    CommandRowMap,
    CommandDdbItem,
    SortKey,
    SortConfig,
} from '../../../types/Commands';

import { parseStatusInfo, getAckPercent } from './CommandsListHelpers';
import { CommandsTable } from './CommandsListTable';
import CommandSubmitForm from '../CommandSubmitForm/CommandSubmitForm';
import DeviceConfigureForm from '../DeviceConfigureForm/DeviceConfigureForm';
import DeviceIdTableFilterBar, {
    deviceIdMatchesTableFilter,
} from '../shared/DeviceIdTableFilterBar';
import { formatApiResponseError } from '../EventsManagementView/utils';
import TableLoadingOverlay from '../shared/TableLoadingOverlay';

// ---- Helpers ----

function uniqStrings(list: string[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of list) {
        const s = String(v || '').trim();
        if (!s) continue;
        if (seen.has(s)) continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}

// ---- Component ----

type CommandsPageProps = {
    operatorScopedEventId?: string | null;
};

export default function CommandsPage(props?: CommandsPageProps): JSX.Element {
    const { operatorScopedEventId = null } = props ?? {};
    const { requestId = '' } = useParams<{ requestId: string }>();
    const navigate = useNavigate();
    const { isAdmin, isOperator } = useAuth();
    const scopedEventId = String(operatorScopedEventId ?? '').trim();

    const [rowsById, setRowsById] = useState<CommandRowMap>({});
    const [allIds, setAllIds] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: 'updatedAt',
        direction: 'desc',
    });

    const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
    const [deviceIdFilter, setDeviceIdFilter] = useState('');
    const [showCommandForm, setShowCommandForm] = useState(false);
    const [showOfflineForm, setShowOfflineForm] = useState(false);

    // Load commands for this request when the route has a requestId.
    useEffect(() => {
        if (!requestId) {
            setError('Missing requestId in route');
            return;
        }

        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);

            try {
                if (isOperator && !isAdmin && !scopedEventId) {
                    throw new Error('Select an event before viewing operator request history.');
                }

                const res =
                    isOperator && !isAdmin
                        ? await window.eventsAPI.getEventRequestCommands(scopedEventId, requestId)
                        : await window.eventsAPI.getRequestCommands(requestId);
                if (cancelled) return;

                if (!res?.ok) {
                    throw new Error(formatApiResponseError(res, 'Failed to load commands for this request'));
                }

                const cmdData = res?.data as { rows?: unknown } | undefined;
                const base = Array.isArray(cmdData?.rows)
                    ? (cmdData.rows as CommandDdbItem[])
                    : [];

                const nextById: CommandRowMap = {};
                const nextIds: string[] = [];

                base.forEach((item) => {
                    const cmdId = item.commandId;
                    if (!cmdId) return;

                    nextById[cmdId] = {
                        ddb: item,
                        ack: parseStatusInfo(item.status_info),
                    };
                    nextIds.push(cmdId);
                });

                setRowsById(nextById);
                setAllIds(nextIds);
                setSelectedDeviceIds([]);
            } catch (err: any) {
                if (cancelled) return;
                setError(err?.message || 'Failed to load commands for this request');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [isAdmin, isOperator, requestId, scopedEventId]);

    // Live updates: merge incoming command updates into rowsById.
    useEffect(() => {
        const unsubscribe = window.fleetAPI.onCommandUpdate((msg: CommandUpdateMessage) => {
            setRowsById((prev) => {
                if (!prev || Object.keys(prev).length === 0) return prev;

                const cmd = (msg as any).command ?? msg;
                const cmdId = cmd.commandId ?? (msg as any).commandId;
                if (!cmdId) return prev;

                const existing = prev[cmdId];
                if (!existing) return prev;

                const nextDdb: CommandDdbItem = {
                    ...existing.ddb,
                    ...cmd,
                };

                const statusInfo =
                    cmd.status_info ??
                    (cmd as any).ack ??
                    existing.ddb.status_info ??
                    existing.ack;

                const nextAck = statusInfo ? parseStatusInfo(statusInfo) : existing.ack;

                const updatedRow: CommandRow = {
                    ddb: nextDdb,
                    ack: nextAck,
                };

                return {
                    ...prev,
                    [cmdId]: updatedRow,
                };
            });
        });

        return () => {
            unsubscribe?.();
        };
    }, []);

    const sortedRows = useMemo(() => {
        if (!sortConfig) {
            return allIds
                .map((id) => ({ id, row: rowsById[id] }))
                .filter((entry) => !!entry.row);
        }

        const { key, direction } = sortConfig;

        const list = allIds
            .map((id) => ({ id, row: rowsById[id] }))
            .filter((entry) => !!entry.row) as { id: string; row: CommandRow }[];

        list.sort((aEntry, bEntry) => {
            const a = aEntry.row;
            const b = bEntry.row;
            const { ddb: ad, ack: aa } = a;
            const { ddb: bd, ack: ba } = b;

            let av: string | number | boolean | undefined;
            let bv: string | number | boolean | undefined;

            switch (key) {
                case 'commandId':
                    av = ad.commandId;
                    bv = bd.commandId;
                    break;
                case 'deviceId':
                    av = ad.deviceId;
                    bv = bd.deviceId;
                    break;
                case 'type':
                    av = ad.type;
                    bv = bd.type;
                    break;
                case 'targetVersion':
                    av = ad.targetVersion;
                    bv = bd.targetVersion;
                    break;
                case 'requestStatusCode':
                    av = ad.requestStatusCode ?? 0;
                    bv = bd.requestStatusCode ?? 0;
                    break;
                case 'status':
                    av = ad.status;
                    bv = bd.status;
                    break;
                case 'ackState':
                    av = aa?.state;
                    bv = ba?.state;
                    break;
                case 'ackReason':
                    av = aa?.reason;
                    bv = ba?.reason;
                    break;
                case 'ackAccepted':
                    av = typeof aa?.accepted === 'boolean' ? Number(aa.accepted) : -1;
                    bv = typeof ba?.accepted === 'boolean' ? Number(ba.accepted) : -1;
                    break;
                case 'ackPercent':
                    av = getAckPercent(aa);
                    bv = getAckPercent(ba);
                    break;
                case 'updatedAt':
                    av = ad.updatedAt ?? 0;
                    bv = bd.updatedAt ?? 0;
                    break;
                default:
                    av = '';
                    bv = '';
            }

            if (typeof av === 'number' && typeof bv === 'number') {
                return direction === 'asc' ? av - bv : bv - av;
            }

            const as = av === undefined || av === null ? '' : String(av);
            const bs = bv === undefined || bv === null ? '' : String(bv);
            return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
        });

        return list;
    }, [rowsById, allIds, sortConfig]);

    const deviceFilteredSortedRows = useMemo(() => {
        return sortedRows.filter((x) =>
            deviceIdMatchesTableFilter(String(x.row?.ddb?.deviceId || ''), deviceIdFilter),
        );
    }, [sortedRows, deviceIdFilter]);

    const visibleDeviceIds = useMemo(() => {
        return uniqStrings(
            deviceFilteredSortedRows.map((x) => x.row?.ddb?.deviceId || '').filter(Boolean) as string[],
        );
    }, [deviceFilteredSortedRows]);

    const selectedSet = useMemo(() => new Set(selectedDeviceIds), [selectedDeviceIds]);

    const allSelected = useMemo(() => {
        if (visibleDeviceIds.length === 0) return false;
        for (const id of visibleDeviceIds) {
            if (!selectedSet.has(id)) return false;
        }
        return true;
    }, [visibleDeviceIds, selectedSet]);

    function requestSort(key: SortKey) {
        setSortConfig((prev) => {
            if (!prev || prev.key !== key) {
                return { key, direction: 'asc' };
            }
            return {
                key,
                direction: prev.direction === 'asc' ? 'desc' : 'asc',
            };
        });
    }

    function getSortIndicator(key: SortKey): string {
        if (!sortConfig || sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    }

    function toggleSelectDevice(deviceId?: string): void {
        const id = String(deviceId || '').trim();
        if (!id) return;

        setSelectedDeviceIds((prev) => {
            const set = new Set(prev);
            if (set.has(id)) set.delete(id);
            else set.add(id);
            return Array.from(set);
        });
    }

    function toggleSelectAllVisible(): void {
        setSelectedDeviceIds((prev) => {
            const set = new Set(prev);
            if (allSelected) {
                for (const id of visibleDeviceIds) set.delete(id);
            } else {
                for (const id of visibleDeviceIds) set.add(id);
            }
            return Array.from(set);
        });
    }

    const hasRows = allIds.length > 0;

    return (
        <div className="h-full flex flex-col p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                    <h1 className="text-lg font-semibold">Commands</h1>
                    <div className="text-xs text-gray-600 dark:text-slate-400 break-all">
                        For request <span className="font-mono">{requestId || '—'}</span>
                    </div>
                </div>

                <button
                    type="button"
                    className="text-xs px-3 py-1 border rounded bg-white dark:bg-slate-900 hover:bg-gray-50"
                    onClick={() => navigate(-1)}
                >
                    Back
                </button>
            </div>

            {error && <div className="mb-2 text-sm text-red-600">Error: {error}</div>}

            {!error && !hasRows && !loading && (
                <div className="text-sm text-gray-500 dark:text-slate-400">No commands found for this request.</div>
            )}

            {!error && hasRows && (
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="mb-2 shrink-0">
                        <DeviceIdTableFilterBar
                            value={deviceIdFilter}
                            onChange={setDeviceIdFilter}
                            enableScanner
                        />
                    </div>
                    <TableLoadingOverlay loading={loading} label="Loading commands..." className="flex-1 min-h-0">
                    <CommandsTable
                        rows={deviceFilteredSortedRows}
                        requestSort={requestSort}
                        getSortIndicator={getSortIndicator}
                        selectedDeviceIds={selectedDeviceIds}
                        allSelected={allSelected}
                        onToggleSelectAll={toggleSelectAllVisible}
                        onToggleSelectDevice={toggleSelectDevice}
                    />
                    </TableLoadingOverlay>

                    <div className="mt-3 self-end flex gap-2">
                        <button
                            className="px-4 py-1.5 border rounded bg-white dark:bg-slate-900 text-xs text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:border-gray-200 dark:border-slate-700"
                            disabled={selectedDeviceIds.length === 0}
                            onClick={() => setShowOfflineForm(true)}
                        >
                            Offline Operations
                        </button>
                        <button
                            className="px-4 py-1.5 border rounded bg-blue-600 text-white text-xs disabled:bg-gray-300 disabled:border-gray-300 dark:border-slate-600"
                            disabled={selectedDeviceIds.length === 0}
                            onClick={() => setShowCommandForm(true)}
                        >
                            Submit Command
                        </button>
                    </div>
                </div>
            )}

            {showCommandForm && (
                <CommandSubmitForm
                    selectedIds={selectedDeviceIds}
                    operatorScopedEventId={isOperator && !isAdmin ? scopedEventId : null}
                    onClose={() => setShowCommandForm(false)}
                    onConfirm={() => {
                        setShowCommandForm(false);
                        setSelectedDeviceIds([]);
                    }}
                />
            )}

            {showOfflineForm && (
                <DeviceConfigureForm
                    selectedIds={selectedDeviceIds}
                    onClose={() => {
                        setShowOfflineForm(false);
                        setSelectedDeviceIds([]);
                    }}
                />
            )}
        </div>
    );
}
